import React, { useState } from 'react';
import { BookOpen, Check, ArrowRight, FileText, Upload, Award } from 'lucide-react';
import { Button } from '../components/ui/button';
import RegisterPopup from '../components/RegisterPopup';
import ServiceProcessSection from '../components/ServiceProcessSection';
import ServiceFAQSection from '../components/ServiceFAQSection';
import SEO from '../components/SEO';

const CopyrightRegistration = () => {
  const [showPopup, setShowPopup] = useState(false);

  const benefits = [
    'Public record of your copyright claim',
    'Ability to sue for infringement in federal court',
    'Evidence in court of validity of copyright',
    'Eligibility for statutory damages and attorney fees',
    'Protection against importation of infringing copies',
    'Foundation for international copyright protection'
  ];

  const protectedWorks = [
    'Literary works (books, articles, manuscripts)',
    'Musical compositions and sound recordings',
    'Dramatic works and screenplays',
    'Choreographic works',
    'Pictorial, graphic, and sculptural works',
    'Motion pictures and audiovisual works',
    'Computer software and digital content',
    'Architectural works'
  ];

  const processSteps = [
    {
      icon: 'FileText',
      title: 'Work Review',
      description: 'We evaluate your creative work to ensure it qualifies for copyright protection and determine the appropriate registration category.'
    },
    {
      icon: 'Upload',
      title: 'Application Preparation',
      description: 'Our team prepares your copyright application with accurate information and proper documentation of your work.'
    },
    {
      icon: 'Award',
      title: 'Registration Certificate',
      description: 'We file with the U.S. Copyright Office and you receive your official registration certificate providing legal proof of ownership.'
    }
  ];

  const icons = {
    FileText: FileText,
    Upload: Upload,
    Award: Award
  };

  const copyrightFAQs = [
    {
      question: 'How long does copyright registration take?',
      answer: 'Standard copyright registration typically takes 3-6 months. We offer expedited processing options that can reduce this to 1-2 months for urgent cases. You receive copyright protection from the moment of creation, but registration provides important legal benefits.'
    },
    {
      question: 'What can I copyright?',
      answer: 'You can copyright original works of authorship including literary works, music, art, photographs, software code, architectural designs, and more. Ideas and facts cannot be copyrighted, only the creative expression of those ideas.'
    },
    {
      question: 'Do I need to copyright my work?',
      answer: 'While copyright exists automatically upon creation, registration is required before you can file a lawsuit for infringement. Registration also provides statutory damages, attorney fees, and public record of your ownership.'
    },
    {
      question: 'How much does copyright registration cost?',
      answer: 'Our copyright registration services start at competitive rates. Government filing fees vary based on the type of work and filing method. We provide transparent pricing with all fees disclosed upfront before you proceed.'
    },
    {
      question: 'How long does copyright protection last?',
      answer: 'For works created after 1978, copyright lasts for the life of the author plus 70 years. For works made for hire, anonymous works, and pseudonymous works, copyright lasts 95 years from publication or 120 years from creation, whichever is shorter.'
    }
  ];

  return (
    <div>
      <SEO 
        title="Copyright Registration Services - Protect Your Creative Works | IP Protection Center"
        description="Professional copyright registration services for authors, artists, musicians, and creators. Fast filing with the U.S. Copyright Office. Protect your original works today."
        keywords="copyright registration, copyright protection, creative works, copyright office, literary works, music copyright, art copyright"
      />
      {/* Hero Section */}
      <section 
        className="relative py-20 bg-cover bg-center"
        style={{
          backgroundImage: `linear-gradient(rgba(0, 0, 0, 0.6), rgba(177, 91, 55, 0.7)), url('/assets/img/inner-bg.jpg')`,
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Copyright Registration Services
            </h1>
            <p className="text-xl text-white/90 mb-8">
              Protect your original creative works with official copyright registration. Secure your intellectual property rights today.
            </p>
            <Button
              onClick={() => setShowPopup(true)}
              size="lg"
              className="bg-white text-brand-500 hover:bg-brand-50 px-8 py-6 text-lg font-semibold"
            >
              Register Your Copyright Today
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </div>
        </div>
      </section>

      {/* What is Copyright Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-aos="zoom-in" data-aos="fade-up">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                What is Copyright Protection?
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                Copyright is a form of legal protection for original works of authorship fixed in a tangible medium of expression. It grants the creator exclusive rights to reproduce, distribute, perform, display, and create derivative works based on the original creation.
              </p>
              <p className="text-lg text-gray-600 mb-6">
                While copyright exists from the moment of creation, registering your copyright with the U.S. Copyright Office provides important legal advantages and is necessary before you can file a lawsuit for infringement.
              </p>
            </div>
            <div className="bg-brand-500 rounded-lg p-8 text-white" data-aos="zoom-in" data-aos="fade-up">
              <BookOpen className="h-16 w-16 mb-4" />
              <h3 className="text-2xl font-bold mb-4">Secure Your Creative Works</h3>
              <p className="mb-6">
                Official copyright registration provides the strongest legal protection for your creative works and original content.
              </p>
              <Button
                onClick={() => setShowPopup(true)}
                className="bg-white text-brand-500 hover:bg-gray-100"
              >
                Get Started Now
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Protected Works Section */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Types of Works We Can Register
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {protectedWorks.map((work, index) => (
                <div key={index} className="flex items-start bg-white p-6 rounded-lg shadow-md">
                  <Check className="h-6 w-6  mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{work}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
              Benefits of Copyright Registration
            </h2>
            <div className="grid md:grid-cols-2 gap-6" data-aos="fade-up" data-aos="zoom-in">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-start bg-gray-50 p-6 rounded-lg" data-aos="zoom-in" data-aos-delay={index * 100}>
                  <Check className="h-6 w-6  mr-3 flex-shrink-0 mt-1" />
                  <p className="text-gray-700">{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <ServiceProcessSection
        title="Our Simple 3-Step Process"
        subtitle="Get your copyright registered in three easy steps"
        steps={processSteps}
        icons={icons}
      />

      {/* FAQ Section */}
      <ServiceFAQSection
        faqs={copyrightFAQs}
        imageSrc="/assets/img/home-sec.jpg"
        onContactClick={() => setShowPopup(true)}
      />

      {/* CTA Section */}
      <section className="py-16 bg-brand-500 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Protect Your Creative Works Today
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Don't leave your original works unprotected. Register your copyright and secure your intellectual property rights.
          </p>
          <Button
            onClick={() => setShowPopup(true)}
            size="lg"
            className="bg-white text-brand-500 hover:bg-gray-100 px-8 py-6 text-lg"
          >
            Get Started Now
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>
        </div>
      </section>

      {showPopup && <RegisterPopup onClose={() => setShowPopup(false)} />}
    </div>
  );
};

export default CopyrightRegistration;